import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { apiFetch } from './api.js';

export interface FilterOption {
  value: string;
  label: string;
}

export interface WorkspaceFilterOptionsResponse {
  cities: FilterOption[];
  neighborhoods: FilterOption[];
  firm_cities?: FilterOption[];
  tags?: FilterOption[];
  statuses?: FilterOption[];
}

export interface AccountFilterOptionsResponse {
  accounts: FilterOption[];
}

export function buildWorkspaceFilterOptionsPath(
  workspaceId: string,
  dataset?: 'account' | 'project' | 'contact',
) {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (dataset) params.set('dataset', dataset);
  return `/filter-options?${params.toString()}`;
}

/** Value lists for the table filter bars: cities and neighborhoods, and this workspace's tags and statuses on a dataset. */
export function useWorkspaceFilterOptions(
  workspaceId: string | undefined,
  dataset?: 'account' | 'project' | 'contact',
) {
  return useQuery({
    queryKey: ['workspace-filter-options', workspaceId, dataset ?? 'project'],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiFetch<WorkspaceFilterOptionsResponse>(
        buildWorkspaceFilterOptionsPath(workspaceId ?? '', dataset),
      ),
    staleTime: 1000 * 60 * 10,
  });
}

function buildAccountFilterOptionsPath(input: {
  workspaceId: string;
  query: string;
  selectedIds: readonly string[];
}) {
  const params = new URLSearchParams({ workspace_id: input.workspaceId });
  if (input.query) params.set('q', input.query);
  for (const selectedId of input.selectedIds) params.append('selected_id', selectedId);
  return `/filter-options/accounts?${params.toString()}`;
}

export function useAccountFilterOptions(input: {
  workspaceId: string | undefined;
  query: string;
  selectedIds: readonly string[];
  enabled: boolean;
}): UseQueryResult<{ accounts: Array<{ value: string; label: string }> }> {
  const query = input.query.trim();
  const selectedIds = [...input.selectedIds].sort();
  return useQuery({
    queryKey: ['account-filter-options', input.workspaceId, query, selectedIds],
    enabled: input.enabled && Boolean(input.workspaceId),
    queryFn: () =>
      apiFetch<AccountFilterOptionsResponse>(
        buildAccountFilterOptionsPath({
          workspaceId: input.workspaceId ?? '',
          query,
          selectedIds,
        }),
      ),
    staleTime: 1000 * 60 * 10,
  });
}
